import {Button, InputBase, Table, TableBody, TableCell, TableRow, Tooltip, styled, IconButton} from '@mui/material';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {kilosToTons, tonsToKilos, localDateSender} from 'helpers/UnitsFormatHandler';
import {useTranslation} from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import {useEffect, useRef} from 'react';
import {TablesDatePicker} from 'components/Generic/DatePickersV2/TablesDatePicker';

interface MAB {
  fromDate: string;
  limit: number;
}

const DATE_PICKER_CLASS = 'mab-datepicker';

export const MABTable = ({
  minDate,
  maxDate,
  mabs,
  onChange,
}: {
  minDate?: Date;
  maxDate?: Date;
  mabs: Array<Partial<MAB>>;
  onChange: (newMabs: Array<Partial<MAB>>) => void;
}) => {
  const tableRef = useRef<HTMLTableElement | null>(null);

  useEffect(() => {
    if (mabs && mabs.length && Object.keys(mabs[mabs.length - 1]).length === 0) {
      const inputFields = tableRef.current?.querySelectorAll<HTMLInputElement>(`.${DATE_PICKER_CLASS}`);
      if (inputFields?.length === mabs.length) {
        inputFields.item(mabs.length - 1).focus();
      }
    }
  }, [mabs]);

  return (
    <MABTableStyled ref={tableRef}>
      <TableBody>
        {mabs.map((mab, i) => (
          <MabRow
            key={i}
            {...{
              mab,
              minDate,
              maxDate,
              onChange: (newMab) => {
                const newMabs = [...mabs];
                newMabs[i] = newMab;
                onChange(newMabs);
              },
              onDelete: () => {
                const newMabs = [...mabs];
                newMabs.splice(i, 1);
                onChange(newMabs);
              },
            }}
          />
        ))}
        <TableRow>
          <MABTableCellStyled
            onClick={() => {
              let fromDate: Date | undefined = minDate;
              const lastDate = mabs[mabs.length - 1]?.fromDate;
              if (mabs.length > 0 && lastDate) {
                fromDate = new Date(lastDate);
                fromDate.setDate(fromDate.getDate() + 1);
              }
              fromDate && onChange([...mabs, {fromDate: localDateSender(fromDate)}]);
            }}
            colSpan={3}
            data-testid="concessions-mab-level-add-button"
            style={{borderBottom: 'none'}}
          >
            <AddMABButtonStyled variant="outlined">
              <AddCircleIcon />
            </AddMABButtonStyled>
          </MABTableCellStyled>
        </TableRow>
      </TableBody>
    </MABTableStyled>
  );
};

const MabRow = ({
  mab,
  onChange,
  onDelete,
  minDate,
  maxDate,
}: {
  mab: Partial<MAB>;
  onChange: (newMab: Partial<MAB>) => void;
  onDelete: () => void;
  minDate?: Date;
  maxDate?: Date;
}) => {
  const {t} = useTranslation('common');

  return (
    <TableRow>
      <MABTableCellStyled>
        <TablesDatePicker
          value={mab.fromDate ? new Date(mab.fromDate) : minDate}
          onChange={(date) => {
            if (!date || isNaN(date.getTime())) {
              return;
            }
            const fromDate = localDateSender(date);
            onChange({...mab, fromDate});
          }}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'concessions-mab-level-date-field',
              },
            },
          }}
        />
      </MABTableCellStyled>
      <MABTableCellStyled>
        <InputBase
          inputComponent={NumberFormatCustom as (props) => JSX.Element}
          value={mab.limit !== undefined ? kilosToTons(mab.limit) : ''}
          placeholder={t('MAB')}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          onChange={({target: {value}}) => {
            const numericValue = Number(value);
            if (!isNaN(numericValue)) {
              onChange({...mab, limit: tonsToKilos(numericValue)});
            }
          }}
          inputProps={{
            'data-testid': 'concessions-mab-level-input-field',
          }}
        />
      </MABTableCellStyled>
      <MABTableCellStyled>
        <Tooltip title={t('remove') ?? ''}>
          <IconButton onClick={onDelete} color="primary" data-testid="concessions-mab-level-delete-button">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </MABTableCellStyled>
    </TableRow>
  );
};

const AddMABButtonStyled = styled(Button)(() => ({
  width: '100%',
  marginBottom: '8px',
}));

const MABTableStyled = styled(Table)(() => ({
  '& input, & button': {
    fontSize: '0.875rem',
    textAlign: 'center',
  },
}));

const MABTableCellStyled = styled(TableCell)(() => ({
  padding: 0,
}));
